// src/authpages/group/FirebaseVideoSetup.js
import { db } from '../../firebase';
import { collection, getDocs, query, limit } from 'firebase/firestore';

/**
 * Helper function to initialize and verify the video call collections
 * This helps diagnose issues with Firebase permissions
 */
export const verifyVideoCallCollections = async () => {
  try {
    console.log("Verifying video call collections existence and permissions...");
    
    // Check if collections exist by attempting to query them
    const roomsQuery = query(collection(db, 'videoCallRooms'), limit(1));
    const signalingQuery = query(collection(db, 'videoCallSignaling'), limit(1));
    
    try {
      // Try to read from rooms collection
      const roomsSnapshot = await getDocs(roomsQuery);
      console.log(`videoCallRooms collection access: SUCCESS (${roomsSnapshot.size} docs found)`);
    } catch (roomError) {
      console.error("videoCallRooms collection access ERROR:", roomError.message);
      throw new Error(`Failed to access videoCallRooms collection: ${roomError.message}`);
    }
    
    try {
      // Try to read from signaling collection
      const signalingSnapshot = await getDocs(signalingQuery);
      console.log(`videoCallSignaling collection access: SUCCESS (${signalingSnapshot.size} docs found)`);
    } catch (signalingError) {
      console.error("videoCallSignaling collection access ERROR:", signalingError.message);
      throw new Error(`Failed to access videoCallSignaling collection: ${signalingError.message}`);
    }
    
    console.log("Video call collections verification complete - all permissions OK");
    return true;
  } catch (error) {
    console.error("Video call collections verification failed:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Call this function before trying to start a video call
 * to ensure Firebase is properly set up
 */
export const initializeVideoCall = async (groupId, userId) => {
  try {
    // First verify collection access
    const verified = await verifyVideoCallCollections();
    
    if (verified !== true) {
      throw new Error("Firebase collections verification failed");
    }
    
    return {
      success: true,
      message: "Video call system initialized successfully"
    };
  } catch (error) {
    console.error("Video call initialization failed:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if browser supports required WebRTC features
 */
export const checkBrowserSupport = () => {
  const supportChecks = {
    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    webRTC: !!(window.RTCPeerConnection),
    mediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices)
  };
  
  const supported = supportChecks.getUserMedia && supportChecks.webRTC;
  
  return {
    supported,
    details: supportChecks,
    browser: {
      name: getBrowserName(),
      version: getBrowserVersion()
    }
  };
};

/**
 * Get the browser name
 */
function getBrowserName() {
  const userAgent = navigator.userAgent;
  
  if (userAgent.indexOf("Firefox") > -1) {
    return "Firefox";
  } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
    return "Opera";
  } else if (userAgent.indexOf("Trident") > -1 || userAgent.indexOf("MSIE") > -1) {
    return "Internet Explorer";
  } else if (userAgent.indexOf("Edge") > -1) {
    return "Edge";
  } else if (userAgent.indexOf("Chrome") > -1) {
    return "Chrome";
  } else if (userAgent.indexOf("Safari") > -1) {
    return "Safari";
  } else {
    return "Unknown";
  }
}

/**
 * Get the browser version (simple implementation)
 */
function getBrowserVersion() {
  const userAgent = navigator.userAgent;
  let offset, version;
  
  if ((offset = userAgent.indexOf("Chrome")) !== -1) {
    return parseFloat(userAgent.substring(offset + 7));
  } else if ((offset = userAgent.indexOf("Firefox")) !== -1) {
    return parseFloat(userAgent.substring(offset + 8));
  } else if ((offset = userAgent.indexOf("Safari")) !== -1) {
    return parseFloat(userAgent.substring(offset + 7));
  } else {
    return "Unknown";
  }
}